import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * The HyperTek prototype's button (h-prototype `components/ui/button.tsx`), class for class, with three
 * product deltas:
 *
 * - `min-h-11 min-w-11` under a thumb. The acceptance matrix asks for a 44 CSS px touch target on every action,
 *   and a link drawn with `buttonVariants()` (or `asChild`) is an anchor the `button, .button` floor in
 *   styles.css does not reach. Under a fine pointer the Button lifts the floor (`COMPACT`) and the sizes are
 *   the compact scale: default 36 px, `sm` 30 px. A `data-primary-action` Button keeps 44 px everywhere, and so
 *   does a link wearing `.button` (styles.css); a bare `buttonVariants()` link keeps it too.
 * - `default` hovers to `--h-primary-hover` instead of `bg-primary/90`. White on primary/90 over white
 *   measures 3.84–4.42:1 for six of the nine palettes (teal, green, indigo, orange, fuchsia, rose); every
 *   `--h-primary-hover` is darker than its primary and so clears 4.5:1 (lib/theme.ts).
 * - `destructive` hovers to `--h-error-fg`: white on destructive/90 measures 4.35:1.
 */
const buttonVariants = cva(
  "inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-[hsl(var(--h-primary-hover))] focus-visible:ring-[hsl(var(--h-primary-hover))] focus-visible:shadow-[inset_0_0_0_3.5px_hsl(var(--h-primary-foreground)/0.9)]',
        destructive:
          'bg-destructive text-white hover:bg-[hsl(var(--h-error-fg))] focus-visible:ring-destructive focus-visible:shadow-[inset_0_0_0_3.5px_rgb(255_255_255/0.9)] dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs focus-visible:border-ring focus-visible:ring-1 hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-3.5 py-1.5 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-[30px] rounded-md gap-1.5 px-2.5 text-[0.8125rem] has-[>svg]:px-2',
        lg: 'h-10 rounded-md px-5 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-[30px]',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/** Lifts the 44 px floor under a mouse. A primary action never takes it: the acceptance matrix measures it at 44. */
const COMPACT = 'pointer-fine:min-h-0 pointer-fine:min-w-0';

type ButtonProps = ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean };

function Button({ className, variant = 'default', size = 'default', asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size }), 'data-primary-action' in props ? null : COMPACT, className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
export type { ButtonProps };
